const toId = require("to-id");
import { nanoid } from "nanoid/non-secure";

import { Meal } from "@/models/Meal.ts";
import { Recipe } from "./Recipe";
import { Ingredient } from "./Ingredient";
import { Persistable } from "@/store/Persistable";
import { getCommonPosition } from "@/models/Geolocation";

type Geolocation = { latitude: number; longitude: number };

abstract class ShoppingListEntry {
  ingredient: Ingredient;
  isShopped: boolean;
  commonShoppingPosition: null | Geolocation;
  shoppedAtPosition: null | Geolocation;
  shoppedAt: null | Date;

  constructor(ingredient: Ingredient) {
    this.ingredient = ingredient;
    this.isShopped = false;
    this.commonShoppingPosition = null;
    this.shoppedAtPosition = null;
    this.shoppedAt = null;
  }

  shop(): void {
    this.isShopped = true;
  }

  toggleIsShopped(): void {
    this.isShopped = !this.isShopped;
  }

  setCommonShoppingPositionFromHistory(
    historyEntries: Array<ShoppingListEntry>
  ): void {
    this.commonShoppingPosition = getCommonPosition(
      historyEntries
        .filter((entry) => entry.ingredient.name === this.ingredient.name)
        .filter((entry) => entry.shoppedAtPosition)
        .map((entry) => entry.shoppedAtPosition),
      50
    );
  }

  intoPureObject(): object {
    return this;
  }
}

class RecipeShoppingListEntry extends ShoppingListEntry implements Persistable {
  recipe: Recipe;
  _id: string;
  _rev: string | null;
  _deleted?: boolean;

  private constructor(ingredient: Ingredient, recipe: Recipe) {
    super(ingredient);
    this.recipe = recipe;
    this._id =
      "RecipeShoppingListEntry:" + toId(this.ingredient.name) + "-" + nanoid();
    this._rev = null;
  }

  static fromRecipe(recipe: Recipe): Array<RecipeShoppingListEntry> {
    return recipe.ingredients.map(
      (ingredient) => new RecipeShoppingListEntry(ingredient, recipe)
    );
  }

  static fromPureObject(data: {
    _id: any;
    _rev: any;
    ingredient: any;
    recipe: any;
    isShopped: any;
    commonShoppingPosition: any;
    shoppedAtPosition: any;
    shoppedAt: any;
  }) {
    if (!data._id || !data.hasOwnProperty("ingredient") || !data.recipe) {
      throw new TypeError("Missing property on raw data");
    }

    let entry = new RecipeShoppingListEntry(
      Ingredient.fromPureObject(data.ingredient),
      data.recipe
    );

    entry._id = data._id;
    entry._rev = data._rev;
    entry.isShopped = data.isShopped;
    entry.commonShoppingPosition = data.commonShoppingPosition;
    entry.shoppedAtPosition = data.shoppedAtPosition;
    entry.shoppedAt = data.shoppedAt ? new Date(data.shoppedAt) : null;
    return entry;
  }
}

class ManualShoppingListEntry extends ShoppingListEntry implements Persistable {
  _id: string;
  _rev: string | null;
  _deleted?: boolean;

  constructor(rawIngredient: string) {
    super(new Ingredient(rawIngredient));
    this._id =
      "ManualShoppingListEntry:" + toId(this.ingredient.name) + "-" + nanoid();
  }

  static fromPureObject(data: {
    _id: any;
    _rev: any;
    ingredient: any;
    isShopped: any;
    commonShoppingPosition: any;
    shoppedAtPosition: any;
    shoppedAt: any;
  }) {
    if (!data._id || !data.hasOwnProperty("ingredient")) {
      throw new TypeError("Missing property on raw data");
    }

    const ingredient = Ingredient.fromPureObject(data.ingredient);
    let entry = new ManualShoppingListEntry(ingredient.name);
    entry.ingredient = ingredient;
    entry._id = data._id;
    entry._rev = data._rev;
    entry.isShopped = data.isShopped;
    entry.commonShoppingPosition = data.commonShoppingPosition;
    entry.shoppedAtPosition = data.shoppedAtPosition;
    entry.shoppedAt = data.shoppedAt ? new Date(data.shoppedAt) : null;
    return entry;
  }
}

export { ShoppingListEntry, RecipeShoppingListEntry, ManualShoppingListEntry };
